import React, { useMemo, useRef } from 'react'
import DropInput from '../../../components/DropInput'
import Card from '../../../components/Card'
import { fabric } from 'fabric'
import cx from 'classnames'
import styles from '../CustomizerPopup.module.css'
import { useTranslation } from 'react-i18next'
import { generateClipPath } from '../../../helpers/generateClipPath'
import { StoredSpot } from '../../../types/Individualization'
import { useCustomizer } from '../../../contexts/CustomizerContext'
import Button from '../../../components/Button'
import { closeModal, openModal } from '../../../services/ModalService'
import MediathekModal from '../SelectMedia/MediathekModal'
import { MediaWithCategories } from '../../../types/Media'
import { api, ApiRoutes } from '../../../services/ApiService'
import InputToolbar from '../common/InputToolbar/InputToolbar'
import { SettingType } from '../../../types/GlobalSetting'
import { usePluginContext } from '../../../contexts/PluginContext'

type ImgElementInputProps = {
    spot: StoredSpot
    uid: string
    onDelete?: (unique_id: string) => void
    showHeading?: boolean
}

const ImgElementInput: React.FC<ImgElementInputProps> = ({
    spot,
    uid,
    onDelete,
    showHeading = true
}) => {
    const {
        userInputs,
        setUserInputs,
        getClipPath,
        activeTemplate,
        subscription,
    } = useCustomizer()

    const { getSettingValue } = usePluginContext()

    const modalId = useRef<string>()

    const getSrc = useMemo(() => {
        const imgElement = userInputs.get(uid)
        return imgElement ? (imgElement as fabric.Image).getSrc() : undefined
    }, [userInputs, uid])

    const selectImage = (media: MediaWithCategories) => {
        if (modalId.current) {
            closeModal(modalId.current)
        }
        api.get(ApiRoutes.MEDIA_BASE64, media.id).then((response) => {
            const path = response.data?.data

            if (!path) return
            changeImage(path)
        })
    }

    const openMediathek = () => {
        const content: JSX.Element = (
            <MediathekModal
                spot={spot}
                uid={uid}
                selectImage={selectImage}
                itemsPerPage={getSettingValue('number_media_elements_per_page', SettingType.INT)}
                showImageTitle={getSettingValue('show_media_image_title', SettingType.BOOLEAN)}
            />
        )
        modalId.current = openModal({
            heading: t('mediaLibrary'),
            content: content,
            size: 'lg',
        })
    }

    const changeImage = (base64: string) => {
        if (!activeTemplate?.canvas) return

        const clipPath = getClipPath(activeTemplate.canvas, spot.id)

        if (!clipPath) return
        activeTemplate.canvas.getObjects().forEach((obj) => {
            if (obj.data?.unique_id === uid && activeTemplate.canvas) {
                activeTemplate.canvas.remove(obj)
            }
        })

        const selectable = !spot.fixed_user_inputs

        fabric.Image.fromURL(base64, (fabricImage: fabric.Image) => {
            const maskW = clipPath.width! * (clipPath.scaleX ?? 1)
            const maskH = clipPath.height! * (clipPath.scaleY ?? 1)
            const imgOriginalW = fabricImage.getOriginalSize().width!
            const imgOriginalH = fabricImage.getOriginalSize().height!

            const scale = Math.min(maskW / imgOriginalW, maskH / imgOriginalH);

            const imgW = imgOriginalW * scale
            const imgH = imgOriginalH * scale

            if (spot.grayscale) {
                fabricImage.filters = [
                    ...(fabricImage.filters ?? []),
                    new fabric.Image.filters.Grayscale(),
                ]
                fabricImage.applyFilters()
            }

            const img = fabricImage.set({
                left: clipPath.left,
                top: clipPath.top,
                angle: clipPath.angle,
                centeredScaling: true,
                originX: 'center',
                originY: 'center',
                hoverCursor: 'initial',
                selectable: selectable,
                evented: selectable,
                hasControls: selectable,
                hasBorders: selectable,
                borderColor: getSettingValue('border_color_spots_in_canvas', SettingType.COLOR) || '#FC0',
                cornerColor: getSettingValue('border_color_spots_in_canvas', SettingType.COLOR) || '#FC0',
                cornerStyle: 'circle',
                transparentCorners: false,
                cornerSize: 12,
                hasRotatingPoint: selectable,
                snapAngle: 1,
                data: {
                    unique_id: uid,
                    spot_id: spot.id,
                    template_id: spot.template_id,
                    size: clipPath.data.size,
                    is_clip_path: false,
                },
            })

            img.setControlsVisibility({
                tl: selectable,
                tr: selectable,
                br: selectable,
                bl: selectable,
                ml: false,
                mt: false,
                mr: false,
                mb: false,
                mtr: selectable,
            })

            img.scaleToWidth(imgW ?? 100)
            img.scaleToHeight(imgH ?? 100)

            img.set('clipPath', generateClipPath(clipPath))

            setUserInputs((prev) => new Map(prev.set(uid, img)))
            activeTemplate.canvas?.add(img)
            activeTemplate.canvas?.setActiveObject(img)
        })
    }

    const { t } = useTranslation(['customizer'])

    return (
        <Card key={spot.id}
              style={{boxShadow: !showHeading ? 'none' : undefined}}
        >
            <Card.Header>
                <div>{ showHeading && spot.name}</div>
                <InputToolbar unique_id={uid} onDelete={onDelete} spot={spot}/>
            </Card.Header>
            <div className={cx(styles['img-input-inner-wrapper'])}>
                <div className={cx(styles['img-input-drop-wrapper'])}>
                    <DropInput
                        key={spot.id}
                        onFileChange={() => spot.media_upload ?? undefined}
                        onBase64Change={(base64: string) =>
                            spot.media_upload ? changeImage(base64) : undefined
                        }
                        accept={'.jpg,.jpeg,.png'}
                        previewSrc={getSrc}
                        disabled={!spot.media_upload}
                    />
                </div>
                <div className={cx(styles['img-input-text-wrapper'])}>
                    {showHeading && spot.hint && <p dangerouslySetInnerHTML={{ __html: spot.hint ?? '' }} />}
                    {subscription &&
                        !!subscription.plans.find(
                            (p) => p.slug === 'zr-customizer-mediathek'
                        ) &&
                        spot.media_library && (
                            <Button
                                hierarchy={'secondary'}
                                type={'button'}
                                text={t('selectFromMediaLibrary')}
                                fn={() => {
                                    openMediathek()
                                }}
                                style={{
                                    padding: '0.5rem 0.5rem',
                                    borderWidth: '2px',
                                }}
                            />
                        )}
                </div>
            </div>
        </Card>
    )
}

export default ImgElementInput
